import { useEffect, useCallback } from "react";

/**
 * useKeyboardShortcuts Hook
 * Manages keyboard shortcuts for POS operations
 *
 * @param {Object} shortcuts - Map of key combinations to handlers
 * @param {boolean} enabled - Whether shortcuts are enabled
 *
 * @example
 * useKeyboardShortcuts({
 *   'Escape': () => closeModal(),
 *   'Enter': () => submitForm(),
 *   'ctrl+p': () => openPayment(),
 * }, true);
 */
export const useKeyboardShortcuts = (shortcuts = {}, enabled = true) => {
  const handleKeyDown = useCallback(
    (event) => {
      if (!enabled) return;

      // Don't trigger shortcuts when typing in inputs
      const isInputField =
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT" ||
        event.target.isContentEditable;

      // Allow Escape even in input fields
      if (isInputField && event.key !== "Escape") return;

      // Build key combination string
      const modifiers = [];
      if (event.ctrlKey || event.metaKey) modifiers.push("ctrl");
      if (event.altKey) modifiers.push("alt");
      if (event.shiftKey) modifiers.push("shift");

      const key = event.key.toLowerCase();
      const combination =
        modifiers.length > 0 ? `${modifiers.join("+")}+${key}` : key;

      // Check for exact match
      if (shortcuts[combination]) {
        event.preventDefault();
        shortcuts[combination](event);
        return;
      }

      // Check for key name match (e.g., "Escape", "Enter")
      if (shortcuts[event.key]) {
        event.preventDefault();
        shortcuts[event.key](event);
      }
    },
    [shortcuts, enabled],
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown, enabled]);
};

/**
 * Keyboard shortcut definitions for POS
 */
export const POS_SHORTCUTS = {
  // Navigation
  ESCAPE: "Escape",
  ENTER: "Enter",

  // Actions
  PAYMENT: "ctrl+p",
  DISCOUNT: "ctrl+d",
  SUBMIT: "ctrl+s",
  ADD_ITEM: "ctrl+a",
  NOTES: "ctrl+n",

  // Quick numbers
  NUM_1: "1",
  NUM_2: "2",
  NUM_3: "3",
  NUM_4: "4",
  NUM_5: "5",
  NUM_6: "6",
  NUM_7: "7",
  NUM_8: "8",
  NUM_9: "9",
  NUM_0: "0",
};

/**
 * Get shortcut display text for UI
 */
export const getShortcutDisplay = (shortcut) => {
  const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;

  return shortcut
    .replace("ctrl", isMac ? "⌘" : "Ctrl")
    .replace("alt", isMac ? "⌥" : "Alt")
    .replace("shift", isMac ? "⇧" : "Shift")
    .replace("+", " + ")
    .toUpperCase();
};

export default useKeyboardShortcuts;
